/**
 * Layout wrapper for Bid Analysis template: exit Dialog, header, sidebar, and main content.
 * All data via props (no context). Store is used by parent (Full) and by form content children.
 */
import React from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { TemplateHeader, TemplateSidebar } from '../shared';
import type { MissingFieldsReport } from '../../shared/types';
import classes from '../../bidAnalysisTemplate.module.scss';

export interface NavigationItem {
  id: string;
  label: string;
  icon: string;
  status?: 'completed' | 'warning' | 'pending';
}

export interface TemplateLayoutProps {
  // Exit confirmation dialog
  pendingNavigation: string | null;
  setPendingNavigation: (path: string | null) => void;
  isBackNavigation: boolean;
  setIsBackNavigation: (v: boolean) => void;
  setIsNavigatingAway: (v: boolean) => void;
  isSaving: boolean;
  navigate: (path: string, opts?: { replace?: boolean }) => void;
  getBackNavigationPath: () => string;
  handleSaveBid: (showToast?: boolean) => Promise<unknown>;
  // Header
  productName: string;
  isSubmittingForReview: boolean;
  progress: number;
  selectedStatus: string;
  bidStatus: string;
  onSave: () => void | Promise<true | undefined>;
  onSubmit: () => void;
  onStatusChange: (newStatus: string) => void;
  isManagerReviewMode: boolean;
  onRequestModification: () => void;
  onApprove: () => void;
  missingFieldsReport: MissingFieldsReport | null;
  onShowMissingFields: () => void;
  templateType: string;
  onRequestNavigation: (path: string) => void;
  // Sidebar
  sidebarCollapsed: boolean;
  onToggleCollapse: () => void;
  navigationItems: NavigationItem[];
  activeSection: string;
  onSectionClick: (sectionId: string) => void;
  productType: 'direct' | 'indirect' | null;
  // Main content
  children: React.ReactNode;
}

export const TemplateLayout: React.FC<TemplateLayoutProps> = ({
  pendingNavigation,
  setPendingNavigation,
  isBackNavigation,
  setIsBackNavigation,
  setIsNavigatingAway,
  isSaving,
  navigate,
  getBackNavigationPath,
  handleSaveBid,
  productName,
  isSubmittingForReview,
  progress,
  selectedStatus,
  bidStatus,
  onSave,
  onSubmit,
  onStatusChange,
  isManagerReviewMode,
  onRequestModification,
  onApprove,
  missingFieldsReport,
  onShowMissingFields,
  templateType,
  onRequestNavigation,
  sidebarCollapsed,
  onToggleCollapse,
  navigationItems,
  activeSection,
  onSectionClick,
  productType,
  children,
}) => {
  return (
    <>
      <Dialog
        visible={pendingNavigation !== null}
        onHide={() => setPendingNavigation(null)}
        header="Exit form?"
        modal
        closable
        footer={
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button
              label="Cancel"
              outlined
              onClick={() => {
                setPendingNavigation(null);
                setIsBackNavigation(false);
                setIsNavigatingAway(false);
              }}
            />
            <Button
              label="Leave without saving"
              severity="danger"
              disabled={isSaving}
              onClick={() => {
                const path = pendingNavigation;
                const isBack = isBackNavigation;
                setIsNavigatingAway(true);
                setPendingNavigation(null);
                setIsBackNavigation(false);
                if (isBack) {
                  navigate(getBackNavigationPath(), { replace: true });
                } else if (path) {
                  navigate(path, { replace: true });
                }
              }}
            />
            <Button
              label="Save and leave"
              onClick={async () => {
                try {
                  await handleSaveBid(false);
                  const path = pendingNavigation;
                  const isBack = isBackNavigation;
                  setIsNavigatingAway(true);
                  setPendingNavigation(null);
                  setIsBackNavigation(false);
                  if (isBack) {
                    navigate(getBackNavigationPath(), { replace: true });
                  } else if (path) {
                    navigate(path, { replace: true });
                  }
                } catch {
                  setIsNavigatingAway(false);
                }
              }}
              disabled={isSaving}
              style={{ backgroundColor: '#522d6e', borderColor: '#522d6e' }}
            />
          </div>
        }
      >
        <p>You might have unsaved changes. Do you really want to exit the form?</p>
      </Dialog>

      <TemplateHeader
        productName={productName}
        isSaving={isSaving}
        isSubmittingForReview={isSubmittingForReview}
        progress={progress}
        currentStatus={selectedStatus}
        onSave={onSave}
        onSubmit={onSubmit}
        onStatusChange={onStatusChange}
        isManagerReviewMode={isManagerReviewMode}
        onRequestModification={onRequestModification}
        onApprove={onApprove}
        bidStatus={bidStatus}
        missingFieldsReport={missingFieldsReport}
        onShowMissingFields={onShowMissingFields}
        templateType={templateType}
        onRequestNavigation={onRequestNavigation}
      />

      <div className={classes.mainLayout}>
        <TemplateSidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={onToggleCollapse}
          navigationItems={navigationItems}
          activeSection={activeSection}
          onSectionClick={onSectionClick}
          templateType={templateType}
          productType={productType}
        />

        <div className={classes.mainContent}>{children}</div>
      </div>
    </>
  );
};
